import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { getClient } from "@vixio/data";
import { closeGateSession } from "../pages/gate";

interface AuthState {
  session: Session | null;
  loading: boolean;
  /** Evento de recuperação de senha (link do e-mail). */
  recovery: boolean;
  aal: "aal1" | "aal2";
  /** A sessão atual entrou com senha (amr "password"). */
  passwordLogin: boolean;
  /** Conta sem senha definida por nós ainda (entrou só por link): o primeiro acesso pede para criar. */
  needsPassword: boolean;
  refreshAal: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

function claimsOf(session: Session | null): { aal?: string; amr?: Array<{ method?: string }> } {
  if (!session) return {};
  try {
    return JSON.parse(atob(session.access_token.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
}

function aalOf(session: Session | null): "aal1" | "aal2" {
  return claimsOf(session).aal === "aal2" ? "aal2" : "aal1";
}

function passwordLoginOf(session: Session | null): boolean {
  return (claimsOf(session).amr ?? []).some((a) => a.method === "password");
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [recovery, setRecovery] = useState(false);

  useEffect(() => {
    const client = getClient();
    let alive = true;
    client.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = client.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === "PASSWORD_RECOVERY") setRecovery(true);
      if (event === "SIGNED_OUT") setRecovery(false);
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      session,
      loading,
      recovery,
      aal: aalOf(session),
      passwordLogin: passwordLoginOf(session),
      needsPassword: !!session && !passwordLoginOf(session) && session.user.user_metadata?.password_set !== true,
      refreshAal: async () => {
        const { data } = await getClient().auth.refreshSession();
        setSession(data.session);
      },
      signOut: async () => {
        // Sair do painel também encerra o acesso aos painéis de infraestrutura (cookie do portão).
        void closeGateSession();
        await getClient().auth.signOut();
        try {
          localStorage.removeItem("vixio.org");
        } catch {
          /* ignore */
        }
      },
    }),
    [session, loading, recovery],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth fora de <AuthProvider>");
  return v;
}
